/**
 * @fileoverview Format dates, durations, phone numbers and audio quality for the Phone Dashboard. Calendar days are computed in the firm's New York time zone, not the browser's, so date ranges match what the server counts.
 */

import api from '../../api';

export const FIRM_TZ = 'America/New_York';

export function firmDate(offsetDays = 0) {
  const now = new Date(Date.now() + offsetDays * 86400000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: FIRM_TZ }).format(now);
}

export function shiftDate(isoDate, days) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const value = new Date(Date.UTC(y, m - 1, d + days));
  return value.toISOString().slice(0, 10);
}

export function daysBetween(from, to) {
  const [a, b] = [from, to].map((v) => Date.UTC(...v.split('-').map((n, i) => (i === 1 ? Number(n) - 1 : Number(n)))));
  return Math.round((b - a) / 86400000) + 1;
}

// The firm-local calendar day of a moment, as 2026-10-16.
export function firmDay(iso) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: FIRM_TZ }).format(new Date(iso));
}

export function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { timeZone: FIRM_TZ, weekday: 'short', month: 'short', day: 'numeric' });
}

export function formatDay(iso) {
  if (!iso) return '';
  const day = firmDay(iso);
  if (day === firmDate()) return 'Today';
  if (day === firmDate(-1)) return 'Yesterday';
  return formatDate(iso);
}

export function formatClock(iso, seconds = false) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', {
    timeZone: FIRM_TZ, hour: 'numeric', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}),
  });
}

export function formatSeconds(value) {
  if (value === null || value === undefined) return '—';
  const total = Math.max(0, Math.round(Number(value)));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  return `${seconds}s`;
}

// A running clock: 0:36, 8:56, 1:02:05.
export function formatTimer(value) {
  const total = Math.max(0, Math.floor(Number(value) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
}

export function formatPhone(value) {
  const text = String(value || '');
  const match = text.match(/^\+?1?(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : text;
}

export function resolveApiHref(path) {
  if (!path) return null;
  const baseUrl = `${api.defaults.baseURL || '/api'}`.replace(/\/$/, '');
  return path.startsWith('/api/') && baseUrl.endsWith('/api') ? `${baseUrl}${path.slice(4)}` : path;
}

export function audioQuality(mos) {
  if (mos === null || mos === undefined) return null;
  if (mos >= 4) return { label: 'Good', tone: 'good' };
  if (mos >= 3.5) return { label: 'Fair', tone: 'fair' };
  return { label: 'Poor', tone: 'poor' };
}

export function partyTitle(call) {
  return call.client?.name || call.party_name || formatPhone(call.party_number) || 'Unknown';
}
